import { ref, watch } from 'vue'
import client from '@/composables/homebaseClient'
import useToast from '@/composables/useToast'

export default function useHomebaseCollectionReceipts(initialParams = {}) {
    const collections = ref([])
    const loading = ref(false)
    const error = ref(null)

    const params = ref({
        status: 'any',
        householdId: '',
        assignedTo: '',
        dateFrom: '',
        dateTo: '',
        limit: 100,
        sortBy: 'requestedAt',
        sortDir: 'desc',
        ...initialParams
    })

    const toQuery = () => {
        const q = new URLSearchParams()
        if (params.value.status) q.set('status', params.value.status)
        if (params.value.householdId) q.set('householdId', params.value.householdId)
        if (params.value.assignedTo) q.set('assignedTo', params.value.assignedTo)
        if (params.value.dateFrom) q.set('dateFrom', params.value.dateFrom)
        if (params.value.dateTo) q.set('dateTo', params.value.dateTo)
        if (params.value.limit) q.set('limit', String(params.value.limit))
        if (params.value.sortBy) q.set('sortBy', params.value.sortBy)
        if (params.value.sortDir) q.set('sortDir', params.value.sortDir)
        return q.toString() ? `?${q.toString()}` : ''
    }

    const fetchCollections = async () => {
        loading.value = true
        error.value = null
        try {
            const res = await client.get(`/collections${toQuery()}`)
            collections.value = Array.isArray(res.data) ? res.data : []
        } catch (err) {
            error.value = err.detail || 'Failed to load collections'
        } finally {
            loading.value = false
        }
    }

    const { showToast } = useToast()

    const exportToCSV = () => {
        if (!collections.value.length) {
            showToast('info', 'No data to export')
            return
        }

        const headers = [
            'Date',
            'Household ID',
            'Container ID',
            'Volume (L)',
            'Performed By',
            'Status'
        ]

        const csvContent = [
            headers.join(','),
            ...collections.value.map(collection => [
                (collection.createdAt || collection.performedAt || '').slice(0, 10),
                collection.householdId || '',
                collection.containerId || '',
                collection.volumeL || '',
                collection.performedBy || collection.assignedTo || '',
                collection.status || ''
            ].join(','))
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `collection-receipts-${new Date().toISOString().slice(0, 10)}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    // Auto-fetch when params change
    watch(params, fetchCollections, { deep: true })

    return {
        collections,
        loading,
        error,
        params,
        fetchCollections,
        exportToCSV
    }
}
